import React, { useState } from 'react';
import { useScrollableMessage } from '../hooks/useScrollableMessage';
import { 
  makeStyles, 
  shorthands,
  Text,
  Button,
  Card,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  SearchBox,
  Dropdown,
  Option,
  Checkbox,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Textarea,
  Field,
  Input,
  Spinner,
} from '@fluentui/react-components';
import { MessageBar, MessageBarTitle } from '../components/Common/ScrollableMessageBar';
import { 
  CheckmarkRegular, 
  DismissRegular, 
  InfoRegular,
  CalendarRegular,
  PersonRegular } from
'@fluentui/react-icons';
import { approveDomain, rejectDomain } from '../services/approvalService';
import { useError } from '../contexts/ErrorContext';
import { DomainPolicyAssignmentWizard, ApprovedDomain, DomainAssignment } from '../components/DomainPolicyAssignment';

const useStyles = makeStyles({
  approvals: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px')
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.borderBottom('3px', 'solid', '#0047AB'),
    ...shorthands.padding('0', '0', '20px', '0'),
    ...shorthands.margin('0', '0', '24px', '0')
  },
  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1a1a1a',
    letterSpacing: '-0.5px'
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    ...shorthands.gap('16px'),
    ...shorthands.margin('0', '0', '16px', '0')
  },
  tableCard: {
    ...shorthands.padding('0'),
    overflow: 'auto' // Allow horizontal scrolling on small screens
  },
  table: {
    minWidth: '1200px', // Set minimum width to prevent cramping
    tableLayout: 'fixed' // Fixed layout for consistent column widths
  },
  bulkActions: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.margin('0', '0', '16px', '0'),
    ...shorthands.padding('12px', '16px'),
    backgroundColor: '#f8f9fa',
    ...shorthands.borderRadius('8px')
  },
  actionButton: {
    minWidth: 'auto',
    ...shorthands.padding('4px', '8px')
  },
  // Column width classes
  checkboxColumn: {
    width: '50px'
  },
  domainColumn: {
    width: '180px'
  },
  typeColumn: {
    width: '120px'
  },
  priorityColumn: {
    width: '80px'
  },
  riskColumn: {
    width: '100px'
  },
  policiesColumn: {
    width: '200px'
  },
  requestorColumn: {
    width: '150px'
  },
  dateColumn: {
    width: '100px'
  },
  actionsColumn: {
    width: '120px'
  }
});

const mockPendingApprovals = [
{
  id: 'app_001',
  domain: 'meridianbank.com',
  organization: 'Meridian Financial',
  requestType: 'New OD Member',
  requestedBy: 'admin@meridianbank.com',
  requestDate: '2024-01-28',
  targetPolicies: ['US Staff OD Policy', 'Default OD Policy'],
  priority: 'High',
  reason: 'New financial institution joining Open Directory network',
  riskAssessment: 'Low',
  verificationStatus: 'Documents Verified'
},
{
  id: 'app_002',
  domain: 'newcreditunion.org',
  organization: 'New Credit Union',
  requestType: 'Domain Addition',
  requestedBy: 'security@yourcorp.com',
  requestDate: '2024-01-29',
  targetPolicies: ['UK Staff OD Policy'],
  priority: 'Medium',
  reason: 'Credit union partnership for European operations',
  riskAssessment: 'Medium',
  verificationStatus: 'Pending Verification'
},
{
  id: 'app_003',
  domain: 'globalbank.asia',
  organization: 'Global Bank Asia',
  requestType: 'Policy Assignment',
  requestedBy: 'admin@yourcorp.com',
  requestDate: '2024-01-30',
  targetPolicies: ['Trader OD Policy'],
  priority: 'Low',
  reason: 'Existing OD member requesting access to trader communications',
  riskAssessment: 'Low',
  verificationStatus: 'Verified'
},
{
  id: 'app_004',
  domain: 'investbank.eu',
  organization: 'European Investment Bank',
  requestType: 'Domain Addition',
  requestedBy: 'compliance@yourcorp.com',
  requestDate: '2024-01-27',
  targetPolicies: ['US Staff OD Policy', 'UK Staff OD Policy'],
  priority: 'High',
  reason: 'Cross-border investment banking collaboration',
  riskAssessment: 'Medium',
  verificationStatus: 'Documents Verified'
},
{
  id: 'app_005',
  domain: 'regionalbank.ca',
  organization: 'Regional Bank Canada',
  requestType: 'New OD Member',
  requestedBy: 'it@regionalbank.ca',
  requestDate: '2024-01-26',
  targetPolicies: ['Default OD Policy'],
  priority: 'Medium',
  reason: 'Regional bank expansion into OD network',
  riskAssessment: 'Low',
  verificationStatus: 'Pending Verification'
}];


export const Approvals: React.FC = () => {
  const styles = useStyles();
  const { showError, showWarning, showInfo } = useError();
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [approvals, setApprovals] = useState(mockPendingApprovals);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [currentApproval, setCurrentApproval] = useState<any>(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [pendingAction, setPendingAction] = useState<'approve' | 'reject' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useScrollableMessage('');
  
  // Domain-to-Policy Assignment Wizard state
  const [isAssignmentWizardOpen, setIsAssignmentWizardOpen] = useState(false);
  const [approvedDomainsForAssignment, setApprovedDomainsForAssignment] = useState<ApprovedDomain[]>([]);

  const handleSelectItem = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedItems([...selectedItems, id]);
    } else {
      setSelectedItems(selectedItems.filter((item) => item !== id));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(filteredApprovals.map((approval) => approval.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleApprove = (approval: any) => {
    setCurrentApproval(approval);
    setPendingAction('approve');
    setApprovalNotes('');
    setConfirmationText('');
    setErrorMessage('');
    setIsConfirmationDialogOpen(true);
  };

  const handleReject = (approval: any) => {
    setCurrentApproval(approval);
    setPendingAction('reject');
    setApprovalNotes('');
    setConfirmationText('');
    setErrorMessage('');
    setIsConfirmationDialogOpen(true);
  };


  // Helper to validate the confirmation text inputs.
  // Returns an error message if the input does not match the required action string ('approve' or 'reject'), otherwise returns null.
  function validateConfirmation(action: 'approve' | 'reject', text: string): string | null {
    if (text.toLowerCase().trim() !== action) {
      return `Must type '${action}' to confirm`;
    }
    return null;
  }

  // Helper to construct the approved domain data object.
  // This is used when an approval is successful and the wizard needs to be opened.
  function getApprovedDomainData(approval: any) {
    return {
      id: approval.id,
      domain: approval.domain,
      organization: approval.organization,
      approvedAt: new Date().toISOString()
    };
  }

  // Helper to call the correct API for approval or rejection
  async function callApprovalApi(action: 'approve' | 'reject', approvalId: string, confirmationText: string, notes?: string) {
    if (action === 'approve') {
      return await approveDomain(approvalId, confirmationText, notes);
    } else {
      return await rejectDomain(approvalId, confirmationText, notes);
    }
  }

  // Main function to process an approval or rejection action.
  // Refactored to reduce cognitive complexity by delegating validation and domain data creation to helper functions.
  // All state updates, error handling, and messaging remain unchanged for test compatibility.
  const processApprovalAction = async () => {
    // Early exit if there is no current approval or action selected.
    if (!currentApproval || !pendingAction) return;

    // Validate the confirmation text using the helper.
    const validationError = validateConfirmation(pendingAction, confirmationText);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    // Indicate that processing is in progress and clear any previous error.
    setIsProcessing(true);
    setErrorMessage('');

    try {
      // Call the correct API using the helper
      await callApprovalApi(pendingAction, currentApproval.id, confirmationText, approvalNotes || undefined);

      // Remove the processed approval from local state.
      setApprovals(approvals.filter((approval) => approval.id !== currentApproval.id));
      setSelectedItems(selectedItems.filter((item) => item !== currentApproval.id));

      // Get the correct text and title for the action using the helper.
      const actionText = pendingAction === 'approve' ? 'approved' : 'rejected';
      const actionTitle = pendingAction === 'approve' ? 'Approved' : 'Rejected';

      // Show a success message to the user.
      showInfo(
        `Domain ${currentApproval.domain} has been ${actionText} successfully`,
        `Domain ${actionTitle}`
      );

      // Close both dialogs.
      setIsConfirmationDialogOpen(false);
      setIsApprovalDialogOpen(false);

      // Prepare wizard data if the action was approval.
      const wasApproval = pendingAction === 'approve';
      const approvedDomainData = wasApproval ? getApprovedDomainData(currentApproval) : null;

      // Reset form and approval state.
      setCurrentApproval(null);
      setPendingAction(null);
      setConfirmationText('');
      setApprovalNotes('');

      // If approval was successful, open the assignment wizard after a short delay.
      if (wasApproval && approvedDomainData) {
        setTimeout(() => {
          setApprovedDomainsForAssignment([approvedDomainData]);
          setIsAssignmentWizardOpen(true);
        }, 150);
      }

      // Log the result for debugging.
      console.log(`${actionTitle}: ${currentApproval.id}`);
    } catch (error: any) {
      // Error handling: show error message and provide a retry option.
      // This matches the original logic for test compatibility.
      const errorMsg = error.message || `Failed to ${pendingAction} domain`;
      const actionTitle = pendingAction === 'approve' ? 'Approval' : 'Rejection';
      setErrorMessage(errorMsg);
      showError({
        message: errorMsg,
        title: `${actionTitle} Failed`,
        severity: 'error',
        details: error?.stack,
        onRetry: () => processApprovalAction()
      });
    } finally {
      // Always clear the processing state at the end.
      setIsProcessing(false);
    }
  };


  const handleBulkApprove = () => {
    setApprovals(approvals.filter((approval) => !selectedItems.includes(approval.id)));
    setSelectedItems([]);
    console.log(`Bulk approved: ${selectedItems.length} items`);
  };

  const handleBulkReject = () => {
    setApprovals(approvals.filter((approval) => !selectedItems.includes(approval.id)));
    setSelectedItems([]);
    console.log(`Bulk rejected: ${selectedItems.length} items`);
  };

  const openApprovalDialog = (approval: any) => {
    setCurrentApproval(approval);
    setApprovalNotes('');
    setIsApprovalDialogOpen(true);
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'High':
        return <Badge appearance="filled" color="danger" data-testid="approvals-high-badge">High</Badge>;
      case 'Medium':
        return <Badge appearance="filled" color="warning" data-testid="approvals-medium-badge">Medium</Badge>;
      case 'Low':
        return <Badge appearance="filled" color="success" data-testid="approvals-low-badge">Low</Badge>;
      default:
        return <Badge appearance="filled" color="subtle" data-testid="approvals-priority-unknown-badge">Unknown</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'New OD Member':
        return <Badge appearance="outline" color="brand" data-testid="approvals-new-member-badge">New Member</Badge>;
      case 'Domain Addition':
        return <Badge appearance="outline" color="success" data-testid="approvals-domain-add-badge">Domain Add</Badge>;
      case 'Policy Assignment':
        return <Badge appearance="outline" color="warning" data-testid="approvals-policy-change-badge">Policy Change</Badge>;
      default:
        return <Badge appearance="outline" color="subtle" data-testid="approvals-other-badge">Other</Badge>;
    }
  };

  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case 'Low':
        return <Badge appearance="tint" color="success" data-testid="approvals-low-risk-badge">Low Risk</Badge>;
      case 'Medium':
        return <Badge appearance="tint" color="warning" data-testid="approvals-medium-risk-badge">Medium Risk</Badge>;
      case 'High':
        return <Badge appearance="tint" color="danger" data-testid="approvals-high-risk-badge">High Risk</Badge>;
      default:
        return <Badge appearance="tint" color="subtle" data-testid="approvals-risk-unknown-badge">Unknown</Badge>;
    }
  };

  const filteredApprovals = approvals.filter((approval) => {
    const matchesSearch = approval.domain.toLowerCase().includes(searchQuery.toLowerCase()) ||
    approval.organization.toLowerCase().includes(searchQuery.toLowerCase()) ||
    approval.reason.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPriority = priorityFilter === 'All' || approval.priority === priorityFilter;
    const matchesType = typeFilter === 'All' || approval.requestType === typeFilter;
    return matchesSearch && matchesPriority && matchesType;
  });

  return (
    <div className={styles.approvals} data-testid="approvals-approvals-div">
      <div className={styles.header} data-testid="approvals-header-div">
        <Text className={styles.title} data-testid="approvals-pending-approvals-text">Pending Approvals</Text>
        <div style={{ display: 'flex', gap: '8px' }} data-testid="approvals-header-stats-div">
          <Text style={{ fontSize: '14px', color: '#605e5c', alignSelf: 'center' }} data-testid="approvals-pending-items-text">
            {filteredApprovals.length} pending items
          </Text>
        </div>
      </div>

      {/* Search and Filters */}
      <div className={styles.controls} data-testid="approvals-controls-div">
        <SearchBox
          placeholder="Search domains, organizations, or reasons..."
          value={searchQuery}
          onChange={(_, data) => setSearchQuery(data.value)}
          style={{ minWidth: '300px' }} data-testid="approvals-search-domains-organizations-or-reasons-search-box" />

        <Dropdown
          placeholder="Priority"
          value={priorityFilter}
          onOptionSelect={(_, data) => setPriorityFilter(data.optionValue || 'All')} data-testid="approvals-priority-dropdown">

          <Option value="All" data-testid="approvals-all-priorities-option">All Priorities</Option>
          <Option value="High" data-testid="approvals-high-option">High</Option>
          <Option value="Medium" data-testid="approvals-medium-option">Medium</Option>
          <Option value="Low" data-testid="approvals-low-option">Low</Option>
        </Dropdown>
        <Dropdown
          placeholder="Type"
          value={typeFilter}
          onOptionSelect={(_, data) => setTypeFilter(data.optionValue || 'All')} data-testid="approvals-type-dropdown">

          <Option value="All" data-testid="approvals-all-types-option">All Types</Option>
          <Option value="New OD Member" data-testid="approvals-new-od-member-option">New OD Member</Option>
          <Option value="Domain Addition" data-testid="approvals-domain-addition-option">Domain Addition</Option>
          <Option value="Policy Assignment" data-testid="approvals-policy-assignment-option">Policy Assignment</Option>
        </Dropdown>
      </div>

      {/* Bulk Actions */}
      {selectedItems.length > 0 &&
      <div className={styles.bulkActions} data-testid="approvals-bulk-actions-div">
          <Text style={{ fontWeight: '600' }} data-testid="approvals-items-selected-text">{selectedItems.length} items selected</Text>
          <Button
          appearance="primary"
          icon={<CheckmarkRegular data-testid="approvals-bulk-checkmark-regular" />}
          onClick={handleBulkApprove} data-testid="approvals-approve-selected-button">

            Approve Selected
          </Button>
          <Button
          appearance="secondary"
          icon={<DismissRegular data-testid="approvals-bulk-dismiss-regular" />}
          onClick={handleBulkReject} data-testid="approvals-reject-selected-button">

            Reject Selected
          </Button>
        </div>
      }

      {/* Approvals Table */}
      <Card className={styles.tableCard} data-testid="approvals-card">
        <Table arial-label="Pending approvals table" className={styles.table} data-testid="approvals-table">
          <TableHeader data-testid="approvals-table-header">
            <TableRow data-testid="approvals-table-row">
              <TableHeaderCell className={styles.checkboxColumn} data-testid="approvals-table-header-cell">
                <Checkbox
                  checked={selectedItems.length === filteredApprovals.length && filteredApprovals.length > 0}
                  onChange={(_, data) => handleSelectAll(data.checked === true)} data-testid="approvals-select-all-checkbox" />

              </TableHeaderCell>
              <TableHeaderCell className={styles.domainColumn} data-testid="approvals-domain-table-header-cell">Domain</TableHeaderCell>
              <TableHeaderCell className={styles.typeColumn} data-testid="approvals-type-table-header-cell">Type</TableHeaderCell>
              <TableHeaderCell className={styles.priorityColumn} data-testid="approvals-priority-table-header-cell">Priority</TableHeaderCell>
              <TableHeaderCell className={styles.riskColumn} data-testid="approvals-risk-table-header-cell">Risk</TableHeaderCell>
              <TableHeaderCell className={styles.policiesColumn} data-testid="approvals-target-policies-table-header-cell">Target Policies</TableHeaderCell>
              <TableHeaderCell className={styles.requestorColumn} data-testid="approvals-requested-by-table-header-cell">Requested By</TableHeaderCell>
              <TableHeaderCell className={styles.dateColumn} data-testid="approvals-date-table-header-cell">Date</TableHeaderCell>
              <TableHeaderCell className={styles.actionsColumn} data-testid="approvals-actions-table-header-cell">Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="approvals-table-body">
            {filteredApprovals.map((approval, approvalIndex) =>
            <TableRow key={approval.id} data-testid={`approvals-table-row-${approvalIndex}`}>
                <TableCell className={styles.checkboxColumn} data-testid={`approvals-checkbox-table-cell-${approvalIndex}`}>
                  <Checkbox
                  checked={selectedItems.includes(approval.id)}
                  onChange={(_, data) => handleSelectItem(approval.id, data.checked === true)} data-testid={`approvals-row-checkbox-${approvalIndex}`} />

                </TableCell>
                <TableCell className={styles.domainColumn} data-testid={`approvals-domain-table-cell-${approvalIndex}`}>
                  <div style={{ wordBreak: 'break-word' }} data-testid={`approvals-domain-cell-content-div-${approvalIndex}`}>
                    <Text style={{
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    display: 'block',
                    lineHeight: '1.2'
                  }} data-testid={`approvals-text-domain-${approvalIndex}`}>
                      {approval.domain}
                    </Text>
                    <Text style={{
                    fontSize: '11px',
                    color: '#605e5c',
                    display: 'block',
                    lineHeight: '1.2',
                    marginTop: '2px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }} data-testid={`approvals-text-organization-${approvalIndex}`}>
                      {approval.organization}
                    </Text>
                  </div>
                </TableCell>
                <TableCell className={styles.typeColumn} data-testid={`approvals-type-table-cell-${approvalIndex}`}>
                  {getTypeBadge(approval.requestType)}
                </TableCell>
                <TableCell className={styles.priorityColumn} data-testid={`approvals-priority-table-cell-${approvalIndex}`}>
                  {getPriorityBadge(approval.priority)}
                </TableCell>
                <TableCell className={styles.riskColumn} data-testid={`approvals-risk-table-cell-${approvalIndex}`}>
                  {getRiskBadge(approval.riskAssessment)}
                </TableCell>
                <TableCell className={styles.policiesColumn} data-testid={`approvals-policies-table-cell-${approvalIndex}`}>
                  <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  maxHeight: '60px',
                  overflow: 'hidden'
                }} data-testid={`approvals-target-policies-preview-div-${approvalIndex}`}>
                    {approval.targetPolicies.slice(0, 2).map((policy, index) =>
                  <Badge key={index} appearance="tint" color="brand" style={{ fontSize: '11px', padding: '2px 6px' }} data-testid={`approvals-badge-${approvalIndex}-${index}`}>
                        {policy.replace(' OD Policy', '')}
                      </Badge>
                  )}
                    {approval.targetPolicies.length > 2 &&
                  <Text style={{ fontSize: '11px', color: '#605e5c' }} data-testid={`approvals-text-more-policies-${approvalIndex}`}>
                        +{approval.targetPolicies.length - 2} more
                      </Text>
                  }
                  </div>
                </TableCell>
                <TableCell className={styles.requestorColumn} data-testid={`approvals-requestor-table-cell-${approvalIndex}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} data-testid={`approvals-requester-cell-div-${approvalIndex}`}>
                    <PersonRegular style={{ fontSize: '14px', color: '#605e5c', flexShrink: 0 }} data-testid={`approvals-person-regular-${approvalIndex}`} />
                    <Text style={{
                    fontSize: '12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }} data-testid={`approvals-text-requested-by-${approvalIndex}`}>
                      {approval.requestedBy}
                    </Text>
                  </div>
                </TableCell>
                <TableCell className={styles.dateColumn} data-testid={`approvals-date-table-cell-${approvalIndex}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} data-testid={`approvals-request-date-cell-div-${approvalIndex}`}>
                    <CalendarRegular style={{ fontSize: '14px', color: '#605e5c' }} data-testid={`approvals-calendar-regular-${approvalIndex}`} />
                    <Text style={{ fontSize: '12px' }} data-testid={`approvals-text-request-date-${approvalIndex}`}>{approval.requestDate}</Text>
                  </div>
                </TableCell>
                <TableCell className={styles.actionsColumn} data-testid={`approvals-actions-table-cell-${approvalIndex}`}>
                  <div style={{ display: 'flex', gap: '4px' }} data-testid={`approvals-row-actions-div-${approvalIndex}`}>
                    <Button
                    appearance="subtle"
                    icon={<InfoRegular data-testid={`approvals-info-regular-${approvalIndex}`} />}
                    size="small"
                    onClick={() => openApprovalDialog(approval)}
                    aria-label="View details" data-testid={`approvals-view-details-button-${approvalIndex}`} />

                    <Button
                    appearance="subtle"
                    icon={<CheckmarkRegular data-testid={`approvals-quick-checkmark-regular-${approvalIndex}`} />}
                    size="small"
                    onClick={() => handleApprove(approval)}
                    aria-label="Quick approve" data-testid={`approvals-quick-approve-button-${approvalIndex}`} />

                    <Button
                    appearance="subtle"
                    icon={<DismissRegular data-testid={`approvals-quick-dismiss-regular-${approvalIndex}`} />}
                    size="small"
                    onClick={() => handleReject(approval)}
                    aria-label="Quick reject" data-testid={`approvals-quick-reject-button-${approvalIndex}`} />

                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Approval Details Dialog */}
      <Dialog open={isApprovalDialogOpen} onOpenChange={(_, data) => setIsApprovalDialogOpen(data.open)} data-testid="approvals-details-dialog">
        <DialogSurface style={{ maxWidth: '600px' }} data-testid="approvals-details-dialog-surface">
          <DialogTitle data-testid="approvals-approval-details-dialog-title">Approval Details</DialogTitle>
          <DialogContent data-testid="approvals-details-dialog-content">
            <DialogBody data-testid="approvals-details-dialog-body">
              {currentApproval &&
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} data-testid="approvals-details-content-div">
                  <div data-testid="approvals-details-domain-row-div">
                    <Text style={{ fontWeight: '600' }} data-testid="approvals-details-domain-text">Domain: </Text>
                    <Text style={{ fontFamily: 'monospace' }} data-testid="approvals-text-current-domain">{currentApproval.domain}</Text>
                  </div>
                  <div data-testid="approvals-details-organization-row-div">
                    <Text style={{ fontWeight: '600' }} data-testid="approvals-details-organization-text">Organization: </Text>
                    <Text data-testid="approvals-text-current-organization">{currentApproval.organization}</Text>
                  </div>
                  <div data-testid="approvals-details-request-type-row-div">
                    <Text style={{ fontWeight: '600' }} data-testid="approvals-request-type-text">Request Type: </Text>
                    {getTypeBadge(currentApproval.requestType)}
                  </div>
                  <div data-testid="approvals-details-priority-row-div">
                    <Text style={{ fontWeight: '600' }} data-testid="approvals-priority-text">Priority: </Text>
                    {getPriorityBadge(currentApproval.priority)}
                  </div>
                  <div data-testid="approvals-details-risk-row-div">
                    <Text style={{ fontWeight: '600' }} data-testid="approvals-risk-assessment-text">Risk Assessment: </Text>
                    {getRiskBadge(currentApproval.riskAssessment)}
                  </div>
                  <div data-testid="approvals-details-verification-row-div">
                    <Text style={{ fontWeight: '600' }} data-testid="approvals-verification-status-text">Verification Status: </Text>
                    <Badge appearance="tint" color="success" data-testid="approvals-badge">{currentApproval.verificationStatus}</Badge>
                  </div>
                  <div data-testid="approvals-details-reason-row-div">
                    <Text style={{ fontWeight: '600' }} data-testid="approvals-reason-text">Reason: </Text>
                    <Text data-testid="approvals-text-reason">{currentApproval.reason}</Text>
                  </div>
                  <div data-testid="approvals-details-target-policies-row-div">
                    <Text style={{ fontWeight: '600' }} data-testid="approvals-target-policies-text">Target Policies: </Text>
                    <div style={{ marginTop: '4px' }} data-testid="approvals-details-target-policies-list-div">
                      {currentApproval.targetPolicies.map((policy: string, index: number) =>
                    <Badge key={index} appearance="tint" color="brand" style={{ margin: '2px' }} data-testid={`approvals-badge-${index}`}>
                          {policy}
                        </Badge>
                    )}
                    </div>
                  </div>
                  <Field label="Approval Notes" data-testid="approvals-approval-notes-field">
                    <Textarea
                    value={approvalNotes}
                    onChange={(_, data) => setApprovalNotes(data.value)}
                    placeholder="Add notes for this approval decision..."
                    rows={3} data-testid="approvals-add-notes-for-this-approval-decision-textarea" />

                  </Field>
                </div>
              }
            </DialogBody>
            <DialogActions data-testid="approvals-details-dialog-actions">
              <Button appearance="secondary" onClick={() => setIsApprovalDialogOpen(false)} data-testid="approvals-details-cancel-button">
                Cancel
              </Button>
              <Button
                appearance="secondary"
                onClick={() => handleReject(currentApproval)} data-testid="approvals-reject-button">

                Reject
              </Button>
              <Button
                appearance="primary"
                onClick={() => handleApprove(currentApproval)} data-testid="approvals-approve-button">

                Approve
              </Button>
            </DialogActions>
          </DialogContent>
        </DialogSurface>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={isConfirmationDialogOpen}
        onOpenChange={(_, data) => {
          if (!data.open) {
            // Dialog is closing - only clear confirmation-related state
            // DO NOT clear wizard state here - it's set after this dialog closes
            setConfirmationText('');
            setApprovalNotes('');
            setErrorMessage('');
            setCurrentApproval(null);
            setPendingAction(null);
          }
          setIsConfirmationDialogOpen(data.open);
        }} data-testid="approvals-confirm-dialog">

        <DialogSurface style={{ maxWidth: '500px' }} data-testid="approvals-confirm-dialog-surface">
          <DialogTitle data-testid="approvals-confirm-dialog-title">
            Confirm {pendingAction === 'approve' ? 'Approval' : 'Rejection'}
          </DialogTitle>
          <DialogContent data-testid="approvals-confirm-dialog-content">
            <DialogBody data-testid="approvals-confirm-dialog-body">
              {currentApproval &&
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} data-testid="approvals-confirmation-content-div">
                  {/* Domain Info */}
                  <div style={{
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e5e5e5'
                }} data-testid="approvals-confirmation-domain-summary-div">
                    <div style={{ marginBottom: '8px' }} data-testid="approvals-confirmation-domain-row-div">
                      <Text style={{ fontWeight: '600', display: 'block' }} data-testid="approvals-confirm-domain-text">Domain:</Text>
                      <Text style={{ fontFamily: 'monospace', fontSize: '14px' }} data-testid="approvals-text-confirmation-current-domain">{currentApproval.domain}</Text>
                    </div>
                    <div style={{ marginBottom: '8px' }} data-testid="approvals-confirmation-organization-row-div">
                      <Text style={{ fontWeight: '600', display: 'block' }} data-testid="approvals-confirm-organization-text">Organization:</Text>
                      <Text data-testid="approvals-text-confirmation-current-organization">{currentApproval.organization}</Text>
                    </div>
                    <div data-testid="approvals-confirmation-risk-row-div">
                      <Text style={{ fontWeight: '600', display: 'block' }} data-testid="approvals-risk-level-text">Risk Level:</Text>
                      {getRiskBadge(currentApproval.riskAssessment)}
                    </div>
                  </div>

                  {/* Confirmation Input */}
                  <Field
                  label={`Type '${pendingAction}' to confirm`}
                  required
                  validationMessage={errorMessage}
                  validationState={errorMessage ? 'error' : 'none'} data-testid="approvals-field">

                    <Input
                    value={confirmationText}
                    onChange={(_, data) => {
                      setConfirmationText(data.value);
                      setErrorMessage(''); // Clear error when user types
                    }}
                    placeholder={`Type '${pendingAction}' to confirm`}
                    disabled={isProcessing} data-testid="approvals-input" />

                  </Field>

                  {/* Optional Notes */}
                  <Field label="Notes (optional)" data-testid="approvals-notes-optional-field">
                    <Textarea
                    value={approvalNotes}
                    onChange={(_, data) => setApprovalNotes(data.value)}
                    placeholder="Add notes for this decision..."
                    rows={3}
                    disabled={isProcessing} data-testid="approvals-add-notes-for-this-decision-textarea" />

                  </Field>

                  {/* Error Message */}
                  {errorMessage &&
                <MessageBar intent="error" style={{ marginTop: '8px' }} data-testid="approvals-message-bar">
                      <MessageBarTitle data-testid="approvals-message-bar-title">{errorMessage}</MessageBarTitle>
                    </MessageBar>
                }
                </div>
              }
            </DialogBody>
            <DialogActions data-testid="approvals-confirm-dialog-actions">
              <Button
                appearance="secondary"
                onClick={() => {
                  // Clear all dialog states in correct order
                  setIsAssignmentWizardOpen(false); // Ensure wizard doesn't open
                  setApprovedDomainsForAssignment([]);
                  setIsConfirmationDialogOpen(false);

                  // Clear form data
                  setConfirmationText('');
                  setApprovalNotes('');
                  setErrorMessage('');

                  // Clear approval context
                  setCurrentApproval(null);
                  setPendingAction(null);
                }}
                disabled={isProcessing} data-testid="approvals-confirm-cancel-button">

                Cancel
              </Button>
              <Button
                appearance={pendingAction === 'approve' ? 'primary' : 'secondary'}
                onClick={processApprovalAction}
                disabled={isProcessing || !confirmationText.trim()}
                icon={isProcessing ? <Spinner size="tiny" data-testid="approvals-spinner" /> :
                pendingAction === 'approve' ? <CheckmarkRegular data-testid="approvals-confirm-checkmark-regular" /> : <DismissRegular data-testid="approvals-confirm-dismiss-regular" />} data-testid="approvals-button">

                {isProcessing ? 'Processing...' :
                pendingAction === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
              </Button>
            </DialogActions>
          </DialogContent>
        </DialogSurface>
      </Dialog>

      {/* Domain-to-Policy Assignment Wizard */}
      <DomainPolicyAssignmentWizard
        approvedDomains={approvedDomainsForAssignment}
        isOpen={isAssignmentWizardOpen}
        onClose={() => {
          const domainCount = approvedDomainsForAssignment.length;
          setIsAssignmentWizardOpen(false);
          setApprovedDomainsForAssignment([]);
          // Show approval complete message when skipping assignment
          showInfo(
            `Successfully approved ${domainCount} domain${domainCount !== 1 ? 's' : ''}`,
            'Approval Complete'
          );
        }}
        onComplete={(assignments: DomainAssignment[]) => {
          console.log('Domain assignments completed:', assignments);

          // Count successful assignments
          const assignedCount = assignments.filter((a) => a.policyIds.length > 0).length;

          if (assignedCount > 0) {
            showInfo(
              `Successfully assigned ${assignedCount} domain${assignedCount !== 1 ? 's' : ''} to policies`,
              'Assignment Complete'
            );
          }

          setIsAssignmentWizardOpen(false);
          setApprovedDomainsForAssignment([]);
        }} data-testid="approvals-domain-policy-assignment-wizard" />


      <div style={{ marginTop: '16px' }} data-testid="approvals-help-text-div">
        <Text style={{ fontSize: '14px', color: '#605e5c' }} data-testid="approvals-review-domain-requests-before-they-re-text">
          💡 Review domain requests before they're added to OD policies. <strong data-testid="approvals-help-high-priority-strong">High priority</strong> items require immediate attention.
        </Text>
      </div>
    </div>);

};